/**
 * Property Owners Page
 * 
 * Information for property owners who want to partner with SUNMARBELLA
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProIcon } from '../components/ProIcon';

export const Owners = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">For Property Owners</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">
            Your Property. Properly Managed.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We work with a small number of Marbella owners who want the right guests, stronger presentation, and local oversight. No volume model. No weak fit.
          </p>
        </motion.div>

        {/* Value Proposition Section */}
        <section className="max-w-5xl mx-auto mb-10 sm:mb-20">
          <h2 className="ui-section-title mb-8 sm:mb-12">Why Owners Choose SUNMARBELLA</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-slate-50 ui-radius-panel p-6 sm:p-8 border border-slate-100 text-center"
            >
              <div className="w-16 h-16 bg-brand-ocean/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <ProIcon icon="house-building" family="light" className="w-8 h-8 text-brand-ocean" />
              </div>
              <h3 className="ui-feature-title mb-3">Stronger Positioning</h3>
              <p className="ui-feature-copy">
                We position your home to attract better-fit guests, not bargain hunters.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-slate-50 ui-radius-panel p-6 sm:p-8 border border-slate-100 text-center"
            >
              <div className="w-16 h-16 bg-brand-ocean/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <ProIcon icon="shield-halved" family="light" className="w-8 h-8 text-brand-ocean" />
              </div>
              <h3 className="ui-feature-title mb-3">Local Oversight</h3>
              <p className="ui-feature-copy">
                We stay close to every stay, spot issues early, and keep standards high.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-slate-50 ui-radius-panel p-6 sm:p-8 border border-slate-100 text-center"
            >
              <div className="w-16 h-16 bg-brand-ocean/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <ProIcon icon="family-dress" family="solid" className="w-8 h-8 text-brand-ocean" />
              </div>
              <h3 className="ui-feature-title mb-3">Better Guest Fit</h3>
              <p className="ui-feature-copy">
                We focus on guests who value well-kept homes, clear communication, and a better stay.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="max-w-5xl mx-auto mb-10 sm:mb-20">
          <h2 className="ui-section-title mb-8 sm:mb-12">What We Handle</h2>
          <div className="bg-slate-50 ui-radius-panel p-8 sm:p-10 border border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                  <ProIcon icon="house-building" family="light" className="w-6 h-6 text-brand-ocean" />
                </div>
                <div>
                  <h3 className="ui-feature-title mb-2">Curated Presentation</h3>
                  <p className="ui-feature-copy">
                    Strong visuals, clear positioning, and sharper copy.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                  <ProIcon icon="chart-line" family="light" className="w-6 h-6 text-brand-ocean" />
                </div>
                <div>
                  <h3 className="ui-feature-title mb-2">Transparent Reporting</h3>
                  <p className="ui-feature-copy">
                    Clear visibility on enquiries, bookings, feedback, and performance.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                  <ProIcon icon="shield-halved" family="light" className="w-6 h-6 text-brand-ocean" />
                </div>
                <div>
                  <h3 className="ui-feature-title mb-2">Local Coordination</h3>
                  <p className="ui-feature-copy">
                    Cleaning, maintenance, and on-the-ground support when something needs doing.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center">
                  <ProIcon icon="family-dress" family="solid" className="w-6 h-6 text-brand-ocean" />
                </div>
                <div>
                  <h3 className="ui-feature-title mb-2">Guest Communication</h3>
                  <p className="ui-feature-copy">
                    Guests deal with a real local team that knows the home and answers fast.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center bg-brand-sand ui-radius-panel p-8 sm:p-12 border border-slate-100">
            <h2 className="ui-title-compact mb-4">Think Your Property Fits?</h2>
            <p className="ui-paragraph mb-8 max-w-2xl mx-auto">
              If you own a strong Marbella property and want a more selective partner, let's talk.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?subject=property-owner" className="ui-btn-primary">
                Start the Conversation
              </Link>
              <Link to="/contact" className="ui-btn-secondary">
                Contact Our Team
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};
